import React, { useState, useEffect } from "react";
import styles from "./AddAppointment.module.scss";
import PrimaryButton from "../../PrimaryButton/PrimaryButton";
import { IconPlus, IconX, IconArrowRight, IconUser } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { Modal, ActionIcon, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { DatePicker } from "@mantine/dates";
import ApiService from "../../../services/api";
import fireToast from "../../../helpers/toaster";
import { PATHS } from "../../../constants/paths";
import { useDebouncedValue } from "@mantine/hooks";
import moment from "moment";

const getOpdIdFromAddResponse = (res) => {
  const d = res?.data?.data ?? res?.data;
  if (d == null) return null;
  if (typeof d === "string") return d;
  return d.opdId ?? d._id ?? d.id ?? null;
};

const formatMobile = (num) => {
  if (!num) return "";
  const cleaned = String(num).replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }
  return num;
};

const isAvailableSlot = (slot) =>
  slot?.isActive &&
  String(slot?.slotStatus || "").toUpperCase() === "AVAILABLE";

const AddAppointment = () => {
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [staffDetails, setStaffDetails] = useState({});
  const [members, setMembers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientsDropdownOpened, setPatientsDropdownOpened] = useState(false);
  const [membersDropdownOpened, setMembersDropdownOpened] = useState(false);
  const [patientSearchValue, setPatientSearchValue] = useState("");
  const [patientSearch, setPatientSearch] = useDebouncedValue(
    patientSearchValue,
    700,
  );
  const navigate = useNavigate();
  const form = useForm({
    initialValues: {
      userId: "",
      staffId: "",
      opdType: "CONSULTATION",
      date: null,
      billingDetails: {
        opdAmount: 0,
        discount: 0,
        tax: 0,
        finalAmount: 0,
        currency: "",
        paymentMethod: "CASH",
        paymentStatus: "COMPLETED",
      },
      opdSource: "WALK_IN",
      slot: {
        slotId: "",
        slotType: "",
        slotNumber: "",
        weekday: "",
        openingTime: "",
        closingTime: "",
      },
    },
  });

  useEffect(() => {
    if (patientsDropdownOpened) {
      callPatientsApi(patientSearch);
    }
  }, [patientSearch, patientsDropdownOpened]);

  useEffect(() => {
    if (membersDropdownOpened) {
      callMembersApi();
    }
  }, [membersDropdownOpened]);

  useEffect(() => {
    const handler = (e) => {
      form.reset();
      setMembers([]);
      setStaffDetails({});
      setPatients([]);
      setPatientSearchValue("");
      setPatientSearch("");
      setPatientsDropdownOpened(false);
      setMembersDropdownOpened(false);

      setOpenModal(true);
      if (e.detail && e.detail.date) {
        form.setFieldValue("date", e.detail.date);
      }
    };
    window.addEventListener("openAddAppointmentDrawer", handler);
    return () =>
      window.removeEventListener("openAddAppointmentDrawer", handler);
  }, [form, setPatientSearch]);

  const callPatientsApi = (search) => {
    ApiService.getOpdPatients({
      page: 1,
      limit: 10,
      search,
    })
      .then((response) => {
        setPatients(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      });
  };

  const callMembersApi = () => {
    ApiService.getStaffList({
      page: 1,
      limit: 100,
      status: "active",
    })
      .then((response) => {
        setMembers(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      });
  };

  const applyPricingFromPayload = (slotTypePricing) => {
    const pricing = slotTypePricing?.[0];
    if (!pricing) {
      form.setFieldValue("billingDetails.opdAmount", 0);
      form.setFieldValue("billingDetails.currency", "");
      form.setFieldValue("billingDetails.tax", 0);
      form.setFieldValue("billingDetails.finalAmount", 0);
      return;
    }
    const price = pricing.price ?? 0;
    const tax = price * 0.05;
    form.setFieldValue("billingDetails.opdAmount", price);
    form.setFieldValue("billingDetails.currency", pricing.currency ?? "");
    form.setFieldValue("billingDetails.tax", tax);
    form.setFieldValue("billingDetails.finalAmount", price + tax);
  };

  /** Calendar day as string — avoids effect deps on `Date` refs (DatePicker creates new refs and retriggers loops). */
  const appointmentDateKey = form.values.date
    ? moment(form.values.date).format("YYYY-MM-DD")
    : "";

  useEffect(() => {
    const staffId = form.values.staffId;
    if (!staffId || !appointmentDateKey) {
      return;
    }

    form.setFieldValue("slot", {
      slotId: "",
      slotType: "",
      slotNumber: "",
      weekday: "",
      openingTime: "",
      closingTime: "",
    });

    setSlotsLoading(true);
    ApiService.getOpdReceptionistAvailableSlots({
      clientId: staffId,
      date: appointmentDateKey,
    })
      .then((response) => {
        const data = response.data.data;
        setStaffDetails(data);
        applyPricingFromPayload(data.slotTypePricing);
      })
      .catch((error) => {
        setStaffDetails({});
        if (error.response) {
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      })
      .finally(() => setSlotsLoading(false));
  }, [form.values.staffId, appointmentDateKey]);

  const validateAppointmentTab = () => {
    const errors = [];
    const values = form.values;

    if (!values.userId) errors.push("Patient is required");
    if (!values.staffId) errors.push("Staff Member is required");
    if (!values.date) errors.push("Date is required");
    if (!values.slot.slotId) errors.push("Slot is required");

    return { isValid: errors.length === 0, errors };
  };

  const handleContinueToPayment = () => {
    const validation = validateAppointmentTab();
    if (!validation.isValid) {
      fireToast("error", validation.errors.join(", "), "bottom-right", true);
      return;
    }

    const selectedUserId = form.values.userId;
    const patient = patients.find(
      (p) =>
        String(p.userId) === String(selectedUserId) ||
        String(p.id) === String(selectedUserId) ||
        String(p._id) === String(selectedUserId),
    );
    const billingPatientName =
      patient?.patientName ?? patient?.name ?? patient?.fullName ?? "";
    const billingUhid =
      patient?.patientId ?? patient?.uhid ?? patient?.UHID ?? "";
    const assignToName = staffDetails?.staffName;
    const appointmentDateIso = form.values.date
      ? moment(form.values.date).toISOString()
      : undefined;
    const staffIdVal = form.values.staffId;
    const slotTypePricingSnapshot = Array.isArray(staffDetails?.slotTypePricing)
      ? staffDetails.slotTypePricing.map((row) => ({ ...row }))
      : undefined;
    const selectedSlotType = form.values.slot.slotType;

    const payload = {
      ...form.values,
      billingDetails: {
        ...form.values.billingDetails,
        paymentStatus: "PENDING",
      },
    };

    setLoading(true);
    ApiService.addAppointment(payload)
      .then((response) => {
        const opdId = getOpdIdFromAddResponse(response);
        if (!opdId) {
          fireToast(
            "error",
            "Appointment created but reference was missing. Check the appointments list.",
            "bottom-right",
            true,
          );
          return;
        }

        const raw = response?.data?.data ?? response?.data;
        const seqNo =
          raw && typeof raw === "object" && raw.seqNo != null
            ? raw.seqNo
            : opdId;

        const navPatientName =
          billingPatientName ||
          (typeof raw === "object" && raw != null
            ? (raw.patientName ?? raw.patient?.patientName ?? "")
            : "");
        const navPatientId =
          billingUhid ||
          (typeof raw === "object" && raw != null
            ? (raw.patientId ??
              raw.uhid ??
              raw.patient?.patientId ??
              raw.patient?.uhid ??
              "")
            : "");

        window.dispatchEvent(new Event("receptionist-appointments-refresh"));

        setOpenModal(false);
        form.reset();
        setMembers([]);
        setStaffDetails({});
        setPatients([]);
        setPatientSearchValue("");
        setPatientsDropdownOpened(false);
        setMembersDropdownOpened(false);

        navigate(PATHS.RECEPTIONIST_COMPLETE_OPD_PAYMENT, {
          state: {
            opdId,
            seqNo,
            patientName: navPatientName || "—",
            patientId: navPatientId || "—",
            uhid: navPatientId || undefined,
            assignToName: assignToName ?? "—",
            staffId: staffIdVal,
            appointmentDate: appointmentDateIso,
            paymentStatus: "PENDING",
            slotTypePricing: slotTypePricingSnapshot,
            selectedSlotType,
          },
        });
      })
      .catch((error) => {
        if (error.response) {
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      })
      .finally(() => setLoading(false));
  };

  const selectedUserId = form.values.userId;
  const selectedPatient = patients.find(
    (p) =>
      String(p.userId) === String(selectedUserId) ||
      String(p.id) === String(selectedUserId) ||
      String(p._id) === String(selectedUserId),
  );

  const closeModal = () => {
    setOpenModal(false);
    setPatientsDropdownOpened(false);
    setMembersDropdownOpened(false);
  };

  return (
    <>
      <PrimaryButton
        text="New Appointment"
        leftSection={<IconPlus />}
        onClick={() => setOpenModal(true)}
      />

      <Modal
        opened={openModal}
        onClose={closeModal}
        centered
        padding={0}
        radius={16}
        size="720px"
        withCloseButton={false}
        overlayProps={{ backgroundOpacity: 0.45, blur: 2 }}
        classNames={{
          content: styles.modal_content,
          body: styles.modal_body,
        }}
      >
        <div className={styles.modal}>
          <div className={styles.modal_header}>
            <div className={styles.heading_container}>
              <p className={styles.heading}>Schedule Appointment</p>
              <p className={styles.desc}>
                Select patient, assign staff, then pick a date and time slot.
              </p>
            </div>
            <ActionIcon
              variant="subtle"
              color="gray"
              radius="md"
              onClick={closeModal}
              aria-label="Close"
            >
              <IconX size={18} />
            </ActionIcon>
          </div>

          <div className={styles.modal_main}>
            <div className={styles.form_grid}>
              {form.values.userId && selectedPatient ? (
                <div className={styles.patient_card}>
                  <div className={styles.patient_card_left}>
                    <div className={styles.avatar}>
                      <IconUser size={18} />
                    </div>
                    <div className={styles.patient_meta}>
                      <span className={styles.patient_name}>
                        {selectedPatient.patientName}
                      </span>
                      <span className={styles.patient_sub}>
                        {selectedPatient.patientId}
                        {selectedPatient.mobileNo
                          ? ` · ${formatMobile(selectedPatient.mobileNo)}`
                          : ""}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.change_btn}
                    onClick={() => form.setFieldValue("userId", "")}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <Select
                  label="Select Patient"
                  placeholder="Search by name, ID, or mobile"
                  data={patients.map((patient) => ({
                    label: patient.patientName,
                    value: patient.userId,
                    name: patient.patientName,
                    patientId: patient.patientId,
                    mobileNo: patient.mobileNo,
                  }))}
                  onChange={(value) => form.setFieldValue("userId", value)}
                  value={form.values.userId}
                  required
                  clearable
                  searchable
                  onSearchChange={setPatientSearchValue}
                  onDropdownOpen={() => setPatientsDropdownOpened(true)}
                  nothingFoundMessage="No results found"
                  renderOption={({ option }) => (
                    <div className={styles.option_row}>
                      <div className={styles.option_left}>
                        <div className={styles.avatar_sm}>
                          <IconUser size={16} />
                        </div>
                        <div className={styles.option_meta}>
                          <span className={styles.option_name}>
                            {option.name}
                          </span>
                          <span className={styles.option_sub}>
                            {option.patientId}
                            {option.mobileNo
                              ? ` · ${formatMobile(option.mobileNo)}`
                              : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  size="md"
                />
              )}

              <Select
                label="Assign To"
                placeholder="Doctor, Therapist, Yoga, or Dietician"
                data={members.map((member) => {
                  const parts = (member.staffId || "").split("-");
                  const uidSuffix = parts[parts.length - 1] || "";
                  const capitalizedRole = member.primaryRole
                    ? member.primaryRole.charAt(0).toUpperCase() +
                      member.primaryRole.slice(1)
                    : "";
                  return {
                    value: member.staffId,
                    label: member.name,
                    name: member.name,
                    role: capitalizedRole,
                    uid: uidSuffix ? `UID-${uidSuffix}` : "",
                  };
                })}
                onChange={(value) => {
                  form.setFieldValue("staffId", value);
                  form.setFieldValue("slot", {
                    slotId: "",
                    slotType: "",
                    slotNumber: "",
                    weekday: "",
                    openingTime: "",
                    closingTime: "",
                  });
                  if (value) {
                    form.setFieldValue("date", form.values.date || new Date());
                  } else {
                    setStaffDetails({});
                  }
                }}
                value={form.values.staffId}
                required
                clearable
                onDropdownOpen={() => setMembersDropdownOpened(true)}
                nothingFoundMessage="No results found"
                renderOption={({ option }) => (
                  <div className={styles.option_row}>
                    <div className={styles.option_left}>
                      <div className={styles.avatar_sm}>
                        <IconUser size={16} />
                      </div>
                      <div className={styles.option_meta}>
                        <span className={styles.option_name}>
                          {option.name}
                        </span>
                        <span className={styles.option_sub}>
                          {option.role}
                          {option.uid ? ` · ${option.uid}` : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                size="md"
              />
            </div>

            {form.values.staffId && (
              <div className={styles.schedule_panel}>
                <div className={styles.date_container}>
                  <p className={styles.section_heading}>Select Date</p>
                  <div className={styles.datepicker_wrap}>
                    <DatePicker
                      {...form.getInputProps("date")}
                      minDate={new Date()}
                    />
                  </div>
                </div>
                <div className={styles.date_container}>
                  <p className={styles.section_heading}>Available Slots</p>
                  <div className={styles.slots}>
                    {slotsLoading ? (
                      <span className={styles.slot_empty}>Loading slots…</span>
                    ) : staffDetails?.slots?.filter(isAvailableSlot)?.length ? (
                      staffDetails.slots
                        .filter(isAvailableSlot)
                        .map((slot, i) => (
                          <button
                            type="button"
                            key={slot._id ?? i}
                            onClick={() => {
                              form.setFieldValue("slot.slotId", slot._id);
                              form.setFieldValue(
                                "slot.slotType",
                                slot.slotType,
                              );
                              form.setFieldValue(
                                "slot.slotNumber",
                                slot.slotNumber,
                              );
                              form.setFieldValue("slot.weekday", slot.weekday);
                              form.setFieldValue(
                                "slot.openingTime",
                                slot.openingTime,
                              );
                              form.setFieldValue(
                                "slot.closingTime",
                                slot.closingTime,
                              );
                            }}
                            className={`${styles.slot} ${
                              form.values.slot.slotId === slot._id
                                ? styles.selected
                                : ""
                            }`}
                          >
                            {slot.openingTime
                              ? moment(slot.openingTime, "HH:mm").format(
                                  "hh:mm A",
                                )
                              : ""}
                          </button>
                        ))
                    ) : (
                      <span className={styles.slot_empty}>
                        No available slots for this date
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.modal_footer}>
            <PrimaryButton
              text="Cancel"
              variant="outline"
              onClick={closeModal}
            />
            <PrimaryButton
              text="Continue"
              rightSection={<IconArrowRight size={18} />}
              onClick={handleContinueToPayment}
              loading={loading}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddAppointment;
